import React from "react";

import {
  Controller,
  useController,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { AgentNodeForm } from "src/agentNode/types";
import { Card } from "src/base-components/Card";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { Switch } from "src/base-components/Switch";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";

type Props = {
  immutable: boolean;
};

const SessionIdOutputLocation: React.FC<Props> = ({ immutable }) => {
  const { control } = useFormContext<AgentNodeForm>();
  const { field } = useController({
    control,
    name: "insights.session_id.mapped_to",
  });

  return (
    <Card className="flex w-full flex-row items-center gap-x-3">
      <div className="flex font-inter-medium-12px text-gray-800">
        Output location
      </div>
      <AutocompleteCodeInput
        dataLoc="agent-node-insights-session-id-mapping"
        disabled={immutable}
        placeholder="session_id"
        prefix="data."
        value={field.value}
        onChange={field.onChange}
      />
    </Card>
  );
};

const SessionIdInsight: React.FC<Props> = ({ immutable }) => {
  const { control } = useFormContext<AgentNodeForm>();
  const { field: activeField } = useController({
    control,
    name: "insights.session_id.active",
  });
  const isActive = useWatch({
    control,
    name: "insights.session_id.active",
  });

  return (
    <div
      className="space-y-2"
      data-loc="agent-node-insights-session-id-container"
    >
      <div className="items-top flex justify-between gap-x-2">
        <div>
          <h3 className="font-inter-semibold-13px text-gray-800">
            Agent run ID
          </h3>
          <span className="font-inter-normal-12px text-gray-500">
            The ID of the Agent execution
          </span>
        </div>
        <Switch
          dataLoc="agent-node-insights-session-id-toggle"
          disabled={immutable}
          enabled={activeField.value}
          onChange={() => activeField.onChange(!activeField.value)}
        />
      </div>
      {isActive && <SessionIdOutputLocation immutable={immutable} />}
    </div>
  );
};

export const AgentResponseMapping: React.FC<Props> = ({ immutable }) => {
  const { control, getValues } = useFormContext<AgentNodeForm>();
  const responseId = getValues("response.default.id");
  const isSearchHighlighted = useIsFieldHighlighted(responseId);

  return (
    <>
      <div className="flex flex-col gap-y-2">
        <span className="font-inter-semibold-13px text-gray-800">Output</span>
        <Card variant="default">
          <Card.Content>
            <div className="flex flex-col gap-y-2">
              <p className="font-inter-normal-12px text-gray-500">
                Agent response will be saved to:
              </p>
              <Controller
                control={control}
                name="response.default.mapped_to"
                render={({ field }) => (
                  <AutocompleteCodeInput
                    dataLoc="agent-response-mapping"
                    disabled={immutable}
                    isSearchHighlighted={isSearchHighlighted}
                    placeholder="agent_response"
                    prefix="data."
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </Card.Content>
        </Card>
      </div>
      <SessionIdInsight immutable={immutable} />
    </>
  );
};
